import { useState } from "react";
import { motion } from "framer-motion";
import { Turnstile } from "@marsidev/react-turnstile";
import SectionHeading from "./SectionHeading";

function FooterForm() {
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const APP_SCRIPT_URL = process.env.REACT_APP_SCRIPT_URL;

  // ================= Animations =================

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 40,
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const fadeLeft = {
    hidden: {
      opacity: 0,
      x: -40,
    },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
      },
    },
  };

  const fadeRight = {
    hidden: {
      opacity: 0,
      x: 40,
    },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
      },
    },
  };

  const scaleIn = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    show: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  // ================= Form Handlers =================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "phone"
          ? value.replace(/\D/g, "").slice(0, 10)
          : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      submit: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim())
      newErrors.name = "Name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Enter a valid email";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Project description is required";
    } else if (formData.message.trim().length < 15) {
      newErrors.message =
        "Please enter at least 15 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value.trim())
          error = "Name is required";
        break;

      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
            value
          )
        ) {
          error = "Enter a valid email";
        }
        break;

      case "phone":
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (!/^[6-9]\d{9}$/.test(value)) {
          error =
            "Enter a valid 10-digit phone number";
        }
        break;

      case "message":
        if (!value.trim()) {
          error = "Project description is required";
        } else if (value.trim().length < 15) {
          error =
            "Please enter at least 15 characters";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // ================= Submit =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!turnstileToken) {
      setErrors((prev) => ({
        ...prev,
        submit: "Please complete verification.",
      }));
      return;
    }

    const payload = {
      source: "Samrat Website",
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      message: formData.message.trim(),
      token: "samrat_secure_2026",
      company: "",
      turnstileToken,
    };

    try {
      setLoading(true);

      const response = await fetch(APP_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        alert("Message sent successfully!");

        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
        });

        setErrors({});
        setTurnstileToken("");
      } else {
        setErrors((prev) => ({
          ...prev,
          submit:
            result.message || "Submission failed.",
        }));
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          "Something went wrong. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="contact"
      className="relative overflow-hidden bg-gradient-to-br from-[#0B061A] via-[#1A0D38] to-[#32005A] py-24"
    >
      {/* Floating Background */}
      <div className="absolute inset-0 overflow-hidden">

        <motion.div
          animate={{
            x: [0, 40, 0],
            y: [0, 30, 0],
            scale: [1, 1.08, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-pink-500/20 blur-[120px]"
        />

        <motion.div
          animate={{
            x: [0, -35, 0],
            y: [0, 20, 0],
            scale: [1, 1.12, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 -translate-y-1/2 -right-32 h-[28rem] w-[28rem] rounded-full bg-purple-600/20 blur-[140px]"
        />

        <motion.div
          animate={{
            y: [0, -25, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-0 left-1/2 -translate-x-1/2 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-[120px]"
        />
      </div>

      {/* Grid Pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,.12) 1px, transparent 0)",
          backgroundSize: "28px 28px",
          opacity: 0.8,
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
                {/* ================= Heading ================= */}

                <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="mb-8 text-center"
        >
          <SectionHeading
            theme="dark"
            badge="Contact Our Team"
            title="Let's Build Something"
            highlight="Amazing Together"
            description="Tell us about your project and we'll help turn your vision into reality."
          />
        </motion.div>

        {/* ================= Glass Card ================= */}

        <motion.div
          variants={scaleIn}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          whileHover={{
            y: -5,
            transition: { duration: 0.35 },
          }}
          className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/95 backdrop-blur-2xl shadow-[0_35px_90px_rgba(0,0,0,.45)]"
        >
          {/* Animated Glow */}
          <motion.div
            animate={{
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-pink-500/5 via-transparent to-purple-500/5"
          />

          <div className="relative p-10">

            {/* Form Heading */}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                Tell us about your project
              </h3>

              <p className="mx-auto mt-3 max-w-2xl leading-7 text-slate-600">
                Whether you need a business website, enterprise software,
                CCTV security, networking, cloud infrastructure or complete
                IT consulting, we're here to help.
              </p>
            </motion.div>

            <form onSubmit={handleSubmit}>

              {/* ================= Name / Email / Phone ================= */}

              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid grid-cols-1 gap-6 lg:grid-cols-3"
              >

                {/* Name */}

                <motion.div variants={fadeLeft}>
                  <label className="mb-2 block text-start text-sm font-semibold text-slate-700">
                    Full Name
                  </label>

                  <motion.input
                    whileFocus={{
                      scale: 1.02,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={(e) =>
                      validateField(e.target.name, e.target.value)
                    }
                    placeholder="John Doe"
                    className={`w-full rounded-2xl border bg-slate-50 px-6 py-3 text-[15px] text-slate-700 outline-none transition-all duration-300 ${
                      errors.name
                        ? "border-red-500 focus:ring-4 focus:ring-red-200"
                        : "border-slate-200 focus:border-pink-500 focus:ring-4 focus:ring-pink-100"
                    }`}
                  />

                  {errors.name && (
                    <motion.p
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-start text-sm text-red-500"
                    >
                      {errors.name}
                    </motion.p>
                  )}
                </motion.div>

                {/* Email */}

                <motion.div variants={fadeUp}>
                  <label className="mb-2 block text-start text-sm font-semibold text-slate-700">
                    Email Address
                  </label>

                  <motion.input
                    whileFocus={{
                      scale: 1.02,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={(e) =>
                      validateField(e.target.name, e.target.value)
                    }
                    placeholder="john@example.com"
                    className={`w-full rounded-2xl border bg-slate-50 px-6 py-3 text-[15px] text-slate-700 outline-none transition-all duration-300 ${
                      errors.email
                        ? "border-red-500 focus:ring-4 focus:ring-red-200"
                        : "border-slate-200 focus:border-pink-500 focus:ring-4 focus:ring-pink-100"
                    }`}
                  />

                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-start text-sm text-red-500"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </motion.div>

                {/* Phone */}

                <motion.div variants={fadeRight}>
                  <label className="mb-2 block text-start text-sm font-semibold text-slate-700">
                    Phone Number
                  </label>

                  <motion.input
                    whileFocus={{
                      scale: 1.02,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    type="tel"
                    name="phone"
                    maxLength={10}
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={(e) =>
                      validateField(e.target.name, e.target.value)
                    }
                    placeholder="+91 9876543210"
                    className={`w-full rounded-2xl border bg-slate-50 px-6 py-3 text-[15px] text-slate-700 outline-none transition-all duration-300 ${
                      errors.phone
                        ? "border-red-500 focus:ring-4 focus:ring-red-200"
                        : "border-slate-200 focus:border-pink-500 focus:ring-4 focus:ring-pink-100"
                    }`}
                  />

                  {errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-start text-sm text-red-500"
                    >
                      {errors.phone}
                    </motion.p>
                  )}
                </motion.div>

              </motion.div>
                            {/* ================= Project Details ================= */}

                            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-6"
              >
                <label className="mb-2 block text-start text-sm font-semibold text-slate-700">
                  Project Details
                </label>

                <motion.textarea
                  whileFocus={{
                    scale: 1.01,
                  }}
                  transition={{
                    duration: 0.25,
                  }}
                  rows={5}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={(e) =>
                    validateField(e.target.name, e.target.value)
                  }
                  placeholder="Describe your business requirements, technologies, expected timeline and any other details..."
                  className={`w-full resize-none rounded-2xl border bg-slate-50 px-6 py-5 text-[15px] leading-7 text-slate-700 outline-none transition-all duration-300 ${
                    errors.message
                      ? "border-red-500 focus:ring-4 focus:ring-red-200"
                      : "border-slate-200 focus:border-pink-500 focus:ring-4 focus:ring-pink-100"
                  }`}
                />

                {errors.message && (
                  <motion.p
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-start text-sm text-red-500"
                  >
                    {errors.message}
                  </motion.p>
                )}
              </motion.div>

              {/* ================= Bottom Section ================= */}

              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-10 border-t border-slate-200 pt-8"
              >
                <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">

                  {/* Left Side */}

                  <motion.div
                    variants={fadeLeft}
                    className="flex flex-col gap-4"
                  >
                    <motion.div
                      whileHover={{
                        scale: 1.02,
                      }}
                      transition={{
                        duration: 0.25,
                      }}
                    >
                      <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={(token) => {
                          setTurnstileToken(token);
                        }}
                        options={{
                          theme: "light",
                          size: "normal",
                        }}
                      />
                    </motion.div>

                    {errors.submit && (
                      <motion.p
                        initial={{
                          opacity: 0,
                          y: -10,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        className="text-sm font-medium text-red-500"
                      >
                        {errors.submit}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* Right Side */}

                  <motion.div
                    variants={fadeRight}
                    className="flex flex-col items-start lg:items-end"
                  >

                    <motion.button
                      whileHover={{
                        scale: 1.05,
                        y: -3,
                        boxShadow:
                          "0 25px 60px rgba(236,72,153,.35)",
                      }}
                      whileTap={{
                        scale: 0.96,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 320,
                        damping: 20,
                      }}
                      type="submit"
                      disabled={loading}
                      className={`group inline-flex items-center justify-center rounded-2xl px-10 py-4 text-[15px] font-semibold text-white transition-all duration-300
                        ${
                          loading
                            ? "cursor-not-allowed bg-gray-400 shadow-none"
                            : "bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-600 shadow-xl"
                        }`}
                    >
                      {loading ? (
                        <>
                          <motion.svg
                            animate={{
                              rotate: 360,
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                            className="mr-3 h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              opacity=".25"
                            />

                            <path
                              d="M22 12a10 10 0 0 1-10 10"
                              stroke="currentColor"
                              strokeWidth="4"
                              strokeLinecap="round"
                            />
                          </motion.svg>

                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message

                          <motion.svg
                            animate={{
                              x: [0, 5, 0],
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                            }}
                            className="ml-3 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M5 12h14"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />

                            <path
                              d="M13 5l7 7-7 7"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </motion.svg>
                        </>
                      )}
                    </motion.button>

                    <motion.p
                      initial={{
                        opacity: 0,
                      }}
                      whileInView={{
                        opacity: 1,
                      }}
                      transition={{
                        delay: 0.5,
                      }}
                      className="mt-4 text-sm text-slate-500"
                    >
                      Average response time:
                      <span className="font-semibold text-pink-600">
                        {" "}
                        Less than 24 Hours
                      </span>
                    </motion.p>
                  </motion.div>

                </div>
              </motion.div>
              </form>
          </div>

          {/* ================= Animated Border Glow ================= */}

          <motion.div
            animate={{
              opacity: [0.15, 0.4, 0.15],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="pointer-events-none absolute inset-0 rounded-[32px] border border-pink-400/20"
          />

          {/* ================= Moving Shine ================= */}

          <motion.div
            animate={{
              x: ["-120%", "220%"],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "linear",
              repeatDelay: 3,
            }}
            className="pointer-events-none absolute inset-y-0 w-32 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent"
          />
        </motion.div>
      </div>
    </div>
  );
}

export default FooterForm;